/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { v4 as uuid_v4 } from "uuid";

import { createEventEmitter, emitEvent } from "@adapptio/lib-shared";
import { Dialog } from "../../../dialogs/Dialog";
import { IEditor } from "../../../IEditor";
import { ResourceService } from "../../../resources/ResourceService";
import { CreateReadmeDialogComponent } from "./CreateReadmeDialogComponent";
import { ToastService } from "../../../toasts/ToastService";
import { TOAST_TYPE } from "../../../toasts/IToast";
import { normalizeResourcePath } from "../../../resources/ResourceHelpers";
import { ReadmeModel } from "../Common/ReadmeModel";
import { ErrorTracker } from "@adapptio/lib-components";

export interface CreateReadmeDialog_Params {
	basePath?: string;
}

export interface CreateReadmeDialog_Result {
	resourceId: string;
}

export interface ICreateReadmeDialog_CreateParams {
	basePath: string;
	name: string;
}

export class CreateReadmeDialog extends Dialog<CreateReadmeDialog_Params, CreateReadmeDialog_Result> {
	/** Services */
	private readonly resourceSvc: ResourceService;
	private readonly toastSvc: ToastService;

	/** New resource ID */
	private readonly resourceId = uuid_v4();

	/** Document model */
	private readmeModel: ReadmeModel;

	/** If readme is being saved */
	private saveInProgress = false;

	/** Event emitted when save progress changes */
	public readonly onSaveStateChange = createEventEmitter<void>();

	/**
	 * Dialog constructor
	 *
	 * @param dc Dependency container
	 * @param id Dialog ID
	 * @param params Open params
	 */
	public constructor(dc: IEditor, id: string, params: CreateReadmeDialog_Params) {
		super(dc, id, params);

		this.resourceSvc = dc.get("resourceService");
		this.toastSvc = dc.get("toastService");

		this.readmeModel = new ReadmeModel();
		this.readmeModel.createDefault();

		const idModel = this.readmeModel.getModel().props.id;
		idModel.schema.setValue(idModel, this.resourceId, false);
	}

	public getComponent(): typeof CreateReadmeDialogComponent {
		return CreateReadmeDialogComponent;
	}

	public getReadmeModel(): ReadmeModel {
		return this.readmeModel;
	}

	/**
	 * Creates a resource
	 * Returns false if create is already in progress or resource was successfully created
	 */
	public async create(params: ICreateReadmeDialog_CreateParams): Promise<boolean> {
		if (this.saveInProgress) {
			return false;
		}

		const content = this.readmeModel.exportDocumentAsYAML();

		this.saveInProgress = true;
		emitEvent(this.onSaveStateChange);

		try {
			await this.resourceSvc.createBlueprint(params.basePath, params.name, content, this.resourceId);

			this.toastSvc.add({
				type: TOAST_TYPE.SUCCESS,
				message: "Readme has been created.",
				timeoutMs: 2500
			});

			this.close({
				resourceId: this.resourceId
			});
		} catch (err) {
			const errorTracker = ErrorTracker.getInstance();
			errorTracker.captureException(err);

			this.toastSvc.add({
				type: TOAST_TYPE.ERROR,
				message: "Failed to create readme. Please try it again in a while.",
				details: String(err)
			});
		} finally {
			this.saveInProgress = false;
			emitEvent(this.onSaveStateChange);
		}

		return true;
	}

	/**
	 * Returns if a readme is being saved
	 */
	public isBeingSaved(): boolean {
		return this.saveInProgress;
	}

	/**
	 * Returns if a resource with given baseName and name already exists
	 *
	 * @param basePath Base path
	 * @param name Name
	 */
	public checkIfBlueprintExists(basePath: string, name: string): boolean {
		return !!this.resourceSvc.getResourceByPath(
			normalizeResourcePath(basePath, name, { sanitize: true })
		);
	}
}
